/**
 * Keeping an open tab on the CURRENT release.
 *
 * Every app in the estate ships a workbox service worker built with
 * `registerType: 'autoUpdate'`, so a new build takes over as soon as the
 * browser notices it. The gap this closes is that **nothing ever made the
 * browser notice**: `registerSW.js` registers once on load and never calls
 * `registration.update()` again, so a tab left open all day keeps serving the
 * bundle it started with — indefinitely. Somebody who leaves the intranet open
 * from Monday to Friday is looking at Monday's release, and a fix shipped on
 * Tuesday reaches them only if they happen to reload.
 *
 * 🔴 **A NEW VERSION IS NEVER FORCED UNDER SOMEBODY'S FINGERS.** Reloading a
 * page silently discards a half-typed announcement, an open modal and every
 * unsaved form on it. So the reload happens automatically only while the tab
 * is HIDDEN — nobody is typing into a background tab — and a visible tab is
 * offered a refresh it can take when it suits.
 */
import { t } from '../i18n/index.js';

const RELOAD_GUARD = 'isg.updateReloaded';
const DEFAULT_INTERVAL = 30 * 60 * 1000;   // half an hour
const MIN_CHECK_GAP = 5 * 60 * 1000;       // don't re-check on every tab focus

let lastCheck = 0;
let started = false;

function check(registration) {
  const now = Date.now();
  if (now - lastCheck < MIN_CHECK_GAP) return;
  lastCheck = now;
  // ⚠️ Swallowed. A failed update check is offline, a proxy, or a restart
  // mid-fetch — none of which is worth an error in a user's console, and
  // none of which should stop the next check.
  registration.update().catch(() => {});
}

function banner(onRefresh) {
  if (document.getElementById('isg-update-pill')) return;
  const el = document.createElement('div');
  el.id = 'isg-update-pill';
  el.setAttribute('role', 'status');
  // ⚠️ LITERAL colours, not tokens. This has to be legible in both themes and
  // in an app whose stylesheet may not have loaded — and the ui-kit grey scale
  // INVERTS under [data-theme="dark"], so a token pair is unreadable in
  // exactly one of them.
  el.style.cssText = [
    'position:fixed', 'left:50%', 'bottom:20px', 'transform:translateX(-50%)',
    'z-index:2147483000', 'display:flex', 'align-items:center', 'gap:12px',
    'padding:10px 14px', 'border-radius:999px',
    'background:#0f172a', 'color:#ffffff',
    'font:600 13px/1.2 system-ui,-apple-system,"Segoe UI",sans-serif',
    'box-shadow:0 6px 24px rgba(0,0,0,0.28)', // i18n-ignore: CSS, not text
  ].join(';');
  const text = document.createElement('span');
  text.textContent = t('appUpdate.available');
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = t('appUpdate.refresh');
  btn.style.cssText = [
    'border:0', 'cursor:pointer', 'padding:6px 14px', 'border-radius:999px',
    'background:#ffffff', 'color:#0f172a',
    'font:600 13px/1.2 system-ui,-apple-system,"Segoe UI",sans-serif',
  ].join(';');
  btn.addEventListener('click', onRefresh);
  el.append(text, btn);
  document.body.appendChild(el);
}

/**
 * Start watching for a newer release.
 *
 * @param {object}  [opts]
 * @param {number}  [opts.interval]  ms between background checks (default 30m)
 * @param {boolean} [opts.autoReloadHidden]  reload a BACKGROUND tab silently
 *        (default true). Set false for an app where a hidden tab may still
 *        hold unsaved state that outlives the page — none does today.
 */
export function initAppUpdate({ interval = DEFAULT_INTERVAL,
                                autoReloadHidden = true } = {}) {
  if (started || typeof navigator === 'undefined'
      || !('serviceWorker' in navigator)) return;
  started = true;

  // 🔴 Captured BEFORE anything awaits. `controllerchange` also fires the
  // very first time a service worker activates on a browser that had none —
  // treating that as "a new release arrived" would reload every user's first
  // ever visit, which reads as the site crashing on them.
  const hadController = !!navigator.serviceWorker.controller;

  const refresh = () => {
    // ⚠️ Guard against a reload loop: never twice within two minutes. A page
    // that reloads forever is far worse than a stale one.
    // 🔴 It used to be ONE reload per tab session, ever (2026-10-09). An
    // INSTALLED app window stays open for days, so it auto-updated once and
    // then never again — the installed intranet was still showing a build
    // from hours and ten releases earlier. A time window stops a loop just
    // as well and lets a long-lived window take every release.
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD) || 0);
      if (last && Date.now() - last < 120000) return;
      sessionStorage.setItem(RELOAD_GUARD, String(Date.now()));
    } catch { /* private mode: accept the small risk of a second reload */ }
    window.location.reload();
  };

  // 🔴 An update that lands while the page is ON SCREEN is applied the next
  // time it goes off screen (another app, another tab, a minimised window).
  // Before 2026-10-09 it only ever showed the pill — and an installed app
  // window is on screen for as long as somebody is using it, so it simply
  // stayed old. Reloading while hidden discards nothing anybody is looking
  // at; reloading under their fingers would discard a half-typed form, which
  // is why the visible case still waits.
  let pending = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return;
    if (autoReloadHidden && document.visibilityState === 'hidden') refresh();
    else { pending = true; banner(refresh); }
  });
  if (autoReloadHidden) {
    document.addEventListener('visibilitychange', () => {
      if (pending && document.visibilityState === 'hidden') refresh();
    });
  }

  navigator.serviceWorker.ready.then((registration) => {
    if (!registration) return;
    check(registration);
    setInterval(() => check(registration), interval);
    // A tab coming back to the foreground is the moment somebody is about to
    // use it, and the cheapest moment to find out it is out of date.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check(registration);
    });
  }).catch(() => {});
}
